/**
 * Profile Component
 * 
 * User profile page with stats, payment history, spend tracking, and security settings.
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WalletConnectSection from './WalletConnectSection';
import CardManagement from './CardManagement';
import '../styles/Profile.css';

const VALID_TABS = ['overview', 'payments', 'spending', 'wallet', 'security'];

export default function Profile() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [spend, setSpend] = useState(null);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab && VALID_TABS.includes(tab) ? tab : 'overview';
  });
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  
  // 2FA state
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaBackupCodes, setTwoFaBackupCodes] = useState(null);
  const [twoFaError, setTwoFaError] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Sync tab when URL search params change (e.g., navigating from UserMenu dropdown)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab('overview');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      fetchData();
      fetch2faStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPayments();
    }
    if (activeTab === 'security') {
      fetch2faStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paymentsPage]);

  const fetch2faStatus = async () => {
    try {
      const res = await fetch('/auth/2fa/status', { credentials: 'include' });
      if (res.ok) {
        setTwoFaStatus(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const start2faSetup = async () => {
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      const res = await fetch('/auth/2fa/setup', {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setTwoFaSetup(await res.json());
      } else {
        setTwoFaError('Failed to start 2FA setup');
      }
    } catch (err) {
      setTwoFaError('Failed to start 2FA setup');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const verify2faSetup = async () => {
    if (!twoFaCode || twoFaCode.length < 6) {
      setTwoFaError('Please enter a valid 6-digit code');
      return;
    }
    
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      const res = await fetch('/auth/2fa/verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: twoFaCode })
      });
      
      if (res.ok) {
        const data = await res.json();
        setTwoFaBackupCodes(data.backup_codes);
        setTwoFaSetup(null);
        setTwoFaCode('');
        fetch2faStatus();
      } else {
        const err = await res.json();
        setTwoFaError(err.detail || 'Invalid verification code');
      }
    } catch (err) {
      setTwoFaError('Verification failed');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const disable2fa = async () => {
    if (!disableCode || disableCode.length < 6) {
      setTwoFaError('Please enter your current 2FA code');
      return;
    }
    
    setTwoFaLoading(true);
    setTwoFaError('');
    try {
      const res = await fetch('/auth/2fa/disable', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: disableCode })
      });
      
      if (res.ok) {
        setShowDisableConfirm(false);
        setDisableCode('');
        fetch2faStatus();
      } else {
        const err = await res.json();
        setTwoFaError(err.detail || 'Failed to disable 2FA');
      }
    } catch (err) {
      setTwoFaError('Failed to disable 2FA');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const cancel2faSetup = () => {
    setTwoFaSetup(null);
    setTwoFaCode('');
    setTwoFaError('');
    setTwoFaBackupCodes(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, spendRes, jobsRes] = await Promise.all([
        fetch('/customers/me/profile', { credentials: 'include' }),
        fetch('/customers/me/spend', { credentials: 'include' }),
        fetch('/customers/me/jobs?status=done', { credentials: 'include' })
      ]);
      
      if (profileRes.ok) {
        setStats(await profileRes.json());
      }
      if (spendRes.ok) {
        setSpend(await spendRes.json());
      }
      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setCompletedJobs(jobsData.jobs || []);
      }
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await fetch(`/customers/me/payments?page=${paymentsPage}&page_size=10`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments);
        setTotalPayments(data.total_count);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };

  const downloadInvoice = async (paymentId) => {
    try {
      const res = await fetch(`/customers/me/payments/${paymentId}/invoice`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${paymentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download invoice');
      }
    } catch (err) {
      console.error('Invoice download error:', err);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div className="profile-title">
            <h1>{user.name || 'User'}</h1>
            <p className="profile-email">{user.email}</p>
          </div>
        </div>

        <div className="profile-tabs">
          <button 
            className={`profile-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`profile-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments
          </button>
          <button 
            className={`profile-tab ${activeTab === 'spending' ? 'active' : ''}`}
            onClick={() => setActiveTab('spending')}
          >
            Spending
          </button>
          <button 
            className={`profile-tab ${activeTab === 'wallet' ? 'active' : ''}`}
            onClick={() => setActiveTab('wallet')}
          >
            Payment Methods
          </button>
          <button 
            className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        </div>

        {loading ? (
          <div className="profile-loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="profile-overview">
                <div className="stats-grid">
                  <div className="stat-card">
                    <span className="stat-value">{stats?.total_jobs || 0}</span>
                    <span className="stat-label">Projects Created</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{formatCurrency(stats?.total_spent_cents || 0)}</span>
                    <span className="stat-label">Total Spent</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{stats?.unique_agents || 0}</span>
                    <span className="stat-label">Agents Worked With</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">✦ {stats?.points?.toLocaleString() || 0}</span>
                    <span className="stat-label">Points Earned</span>
                  </div>
                </div>

                <div className="profile-section">
                  <h3>Account Details</h3>
                  <div className="profile-field">
                    <label>Email</label>
                    <span>{stats?.email}</span>
                  </div>
                  {stats?.name && (
                    <div className="profile-field">
                      <label>Name</label>
                      <span>{stats.name}</span>
                    </div>
                  )}
                  <div className="profile-field">
                    <label>Member Since</label>
                    <span>{stats?.member_since ? formatDate(stats.member_since) : '—'}</span>
                  </div>
                </div>

                {completedJobs.length > 0 && (
                  <div className="profile-section">
                    <h3>Completed Projects</h3>
                    <div className="completed-jobs-list">
                      {completedJobs.map(job => (
                        <Link 
                          key={job.id} 
                          to={`/job/${job.id}`}
                          className="completed-job-card"
                        >
                          <div className="completed-job-info">
                            <span className="completed-job-title">
                              {job.goal?.slice(0, 60) || 'Untitled Project'}
                              {job.goal?.length > 60 ? '...' : ''}
                            </span>
                            <span className="completed-job-date">
                              {job.completed_at ? formatDate(job.completed_at) : ''}
                            </span>
                          </div>
                          <span className="completed-job-arrow">View Details →</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="profile-payments">
                <h3>Payment History</h3>
                {payments.length === 0 ? (
                  <p className="empty-state">No payments yet</p>
                ) : (
                  <>
                    <table className="payments-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Project</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map(payment => (
                          <tr key={payment.job_id || payment.id}>
                            <td>{formatDate(payment.date)}</td>
                            <td>
                              {formatCurrency(payment.amount_cents)}
                              <span className={`payment-method-badge ${payment.payment_method}`}>
                                {payment.payment_method === 'usdc' ? 'USDC' : 'Card'}
                              </span>
                            </td>
                            <td>
                              <span className={`payment-status ${payment.status}`}>
                                {payment.status === 'succeeded' && payment.refunded_cents > 0
                                  ? `Partially refunded ($${(payment.refunded_cents / 100).toFixed(2)})`
                                  : ({ succeeded: 'Paid', refunded: 'Refunded', pending: 'Pending', failed: 'Failed' }[payment.status] || payment.status)}
                              </span>
                            </td>
                            <td>
                              {payment.conversation_id ? (
                                <a href={`/chat/${payment.conversation_id}`} className="payment-link">
                                  View →
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              {payment.status === 'succeeded' && payment.payment_method === 'usdc' ? (
                                payment.reference ? (
                                  <a
                                    href={`https://basescan.org/tx/${payment.reference}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="basescan-link"
                                    title="View on BaseScan"
                                  >
                                    🔗
                                  </a>
                                ) : (
                                  <span className="tx-unavailable" title="Transaction hash unavailable">—</span>
                                )
                              ) : payment.status === 'succeeded' && payment.id ? (
                                <button
                                  className="invoice-download-btn"
                                  onClick={() => downloadInvoice(payment.id)}
                                  title="Download Invoice"
                                >
                                  ⬇
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {totalPayments > 10 && (
                      <div className="pagination">
                        <button 
                          disabled={paymentsPage === 1}
                          onClick={() => setPaymentsPage(p => p - 1)}
                        >
                          ← Previous
                        </button>
                        <span>Page {paymentsPage} of {Math.ceil(totalPayments / 10)}</span>
                        <button 
                          disabled={paymentsPage >= Math.ceil(totalPayments / 10)}
                          onClick={() => setPaymentsPage(p => p + 1)}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'spending' && (
              <div className="profile-spending">
                <div className="spend-summary">
                  <h3>Lifetime Spending</h3>
                  <p className="spend-total">{formatCurrency(spend?.total_lifetime_cents || 0)}</p>
                </div>

                {spend?.monthly_breakdown?.length > 0 && (
                  <div className="spend-section">
                    <h4>Monthly Breakdown</h4>
                    <ul className="spend-list">
                      {spend.monthly_breakdown.map(month => (
                        <li key={month.month} className="spend-item">
                          <span className="spend-month">{month.month}</span>
                          <span className="spend-amount">{formatCurrency(month.amount_cents)}</span>
                          <span className="spend-jobs">{month.job_count} project{month.job_count !== 1 ? 's' : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {spend?.by_conversation?.length > 0 && (
                  <div className="spend-section">
                    <h4>By Project</h4>
                    <ul className="spend-list">
                      {spend.by_conversation.map(conv => (
                        <li key={conv.conversation_id} className="spend-item">
                          <a href={`/chat/${conv.conversation_id}`} className="spend-project">
                            {conv.name}
                          </a>
                          <span className="spend-amount">{formatCurrency(conv.amount_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!spend?.monthly_breakdown?.length && !spend?.by_conversation?.length && (
                  <p className="empty-state">No spending history yet</p>
                )}
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="profile-wallet">
                <h3>Payment Methods</h3>
                
                {/* Card Management */}
                <CardManagement />
                
                <WalletConnectSection 
                  customer={stats}
                  onUpdate={fetchData}
                />
              </div>
            )}

            {activeTab === 'security' && (
              <div className="profile-security">
                <h3>Two-Factor Authentication</h3>
                
                {twoFaError && (
                  <div className="twofa-error">{twoFaError}</div>
                )}

                {/* Show backup codes after successful setup */}
                {twoFaBackupCodes && (
                  <div className="twofa-backup-codes">
                    <div className="backup-codes-header">
                      <span className="backup-icon">🔐</span>
                      <h4>Save Your Backup Codes</h4>
                    </div>
                    <p className="backup-codes-warning">
                      Store these codes in a safe place. Each code can only be used once.
                      If you lose access to your authenticator app, use these to log in.
                    </p>
                    <div className="backup-codes-grid">
                      {twoFaBackupCodes.map((code, i) => (
                        <code key={i} className="backup-code">{code}</code>
                      ))}
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setTwoFaBackupCodes(null)}
                    >
                      I've saved my backup codes
                    </button>
                  </div>
                )}

                {/* 2FA Status when not in setup */}
                {!twoFaSetup && !twoFaBackupCodes && (
                  <div className="twofa-status">
                    {twoFaStatus?.enabled ? (
                      <>
                        <div className="twofa-enabled">
                          <span className="status-badge enabled">✓ Enabled</span>
                          <p>Your account is protected with two-factor authentication.</p>
                          {twoFaStatus.backup_codes_remaining !== undefined && (
                            <p className="backup-remaining">
                              Backup codes remaining: <strong>{twoFaStatus.backup_codes_remaining}</strong>
                            </p>
                          )}
                        </div>
                        
                        {!showDisableConfirm ? (
                          <button 
                            className="btn btn-danger-outline"
                            onClick={() => setShowDisableConfirm(true)}
                          >
                            Disable 2FA
                          </button>
                        ) : (
                          <div className="twofa-disable-confirm">
                            <p>Enter your current 2FA code to disable:</p>
                            <input
                              type="text"
                              value={disableCode}
                              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="000000"
                              className="twofa-input"
                              maxLength={6}
                            />
                            <div className="twofa-actions">
                              <button 
                                className="btn btn-danger"
                                onClick={disable2fa}
                                disabled={twoFaLoading}
                              >
                                {twoFaLoading ? 'Disabling...' : 'Confirm Disable'}
                              </button>
                              <button 
                                className="btn btn-secondary"
                                onClick={() => {
                                  setShowDisableConfirm(false);
                                  setDisableCode('');
                                  setTwoFaError('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="twofa-disabled">
                          <span className="status-badge disabled">Not enabled</span>
                          <p>Add an extra layer of security to your account by enabling two-factor authentication.</p>
                        </div>
                        <button 
                          className="btn btn-primary"
                          onClick={start2faSetup}
                          disabled={twoFaLoading}
                        >
                          {twoFaLoading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* 2FA Setup Flow */}
                {twoFaSetup && !twoFaBackupCodes && (
                  <div className="twofa-setup">
                    <div className="setup-step">
                      <h4>1. Scan QR Code</h4>
                      <p>Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and scan this QR code:</p>
                      <div className="qr-container">
                        <img src={twoFaSetup.qr_code} alt="2FA QR Code" className="qr-code" />
                      </div>
                      <details className="manual-entry">
                        <summary>Can't scan? Enter manually</summary>
                        <code className="manual-key">{twoFaSetup.manual_entry_key}</code>
                      </details>
                    </div>
                    
                    <div className="setup-step">
                      <h4>2. Enter Verification Code</h4>
                      <p>Enter the 6-digit code from your authenticator app:</p>
                      <input
                        type="text"
                        value={twoFaCode}
                        onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="twofa-input"
                        maxLength={6}
                        autoFocus
                      />
                      <div className="twofa-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={verify2faSetup}
                          disabled={twoFaLoading || twoFaCode.length < 6}
                        >
                          {twoFaLoading ? 'Verifying...' : 'Verify & Enable'}
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={cancel2faSetup}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
